/**
 * Policy/config version diff (ADMIN-05). Pure. Flattens nested objects to dot
 * paths and compares two versions key by key. Arrays compare by value (JSON).
 */

const isPlainObject = (v) => v !== null && typeof v === 'object' && !Array.isArray(v);

/** { a: { b: 1 }, c: [1] } → { 'a.b': 1, c: [1] } */
export function flatten(obj, prefix = '', out = {}) {
  for (const [k, v] of Object.entries(obj || {})) {
    const path = prefix ? `${prefix}.${k}` : k;
    if (isPlainObject(v) && Object.keys(v).length > 0) flatten(v, path, out);
    else out[path] = v;
  }
  return out;
}

const same = (a, b) => JSON.stringify(a) === JSON.stringify(b);

/**
 * @returns Array<{ path, change: 'added'|'removed'|'changed'|'unchanged', before, after }>
 * sorted by path. `before`/`after` are undefined when the key is absent.
 */
export function diffPolicy(before, after) {
  const a = flatten(before);
  const b = flatten(after);
  const paths = [...new Set([...Object.keys(a), ...Object.keys(b)])].sort();
  return paths.map((path) => {
    const inA = Object.prototype.hasOwnProperty.call(a, path);
    const inB = Object.prototype.hasOwnProperty.call(b, path);
    let change = 'unchanged';
    if (!inA) change = 'added';
    else if (!inB) change = 'removed';
    else if (!same(a[path], b[path])) change = 'changed';
    return { path, change, before: a[path], after: b[path] };
  });
}

/** Counts per change type, e.g. { added: 1, removed: 0, changed: 2, unchanged: 7 }. */
export function summarizeDiff(diff) {
  const out = { added: 0, removed: 0, changed: 0, unchanged: 0 };
  for (const d of diff) out[d.change] += 1;
  return out;
}

/** Render a flattened value for display. */
export function formatPolicyValue(v) {
  if (v === undefined) return '—';
  if (v === null) return 'null';
  if (Array.isArray(v)) return v.length ? v.join(', ') : '[]';
  if (typeof v === 'boolean') return v ? 'true' : 'false';
  return String(v);
}

/** Inverse of flatten: { 'a.b': 1 } → { a: { b: 1 } }. */
export function unflatten(flat) {
  const out = {};
  for (const [path, v] of Object.entries(flat || {})) {
    const keys = path.split('.');
    let node = out;
    keys.slice(0, -1).forEach((k) => {
      if (!isPlainObject(node[k])) node[k] = {};
      node = node[k];
    });
    node[keys[keys.length - 1]] = v;
  }
  return out;
}

/**
 * Parse an edited text value back to the type of the original (`like`).
 * Returns { ok: true, value } or { ok: false, message }.
 */
export function coerceLike(text, like) {
  const s = String(text == null ? '' : text).trim();
  if (typeof like === 'number') {
    if (s === '' || !Number.isFinite(Number(s))) return { ok: false, message: 'Must be a number' };
    return { ok: true, value: Number(s) };
  }
  if (typeof like === 'boolean') {
    if (s !== 'true' && s !== 'false') return { ok: false, message: 'Must be true or false' };
    return { ok: true, value: s === 'true' };
  }
  if (Array.isArray(like)) return { ok: true, value: s ? s.split(',').map((x) => x.trim()).filter(Boolean) : [] };
  return { ok: true, value: s };
}

/** Next semantic-ish version label: 'v3' → 'v4'. */
export function nextVersion(label) {
  const m = /^v(\d+)$/.exec(String(label || ''));
  return m ? `v${Number(m[1]) + 1}` : 'v1';
}
